import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Alert, Snackbar, Typography, useMediaQuery, useTheme,
  Drawer, Fab,
} from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import ReportBuilderToolbar from './reportBuilder/ReportBuilderToolbar';
import ReportBuilderGrid from './reportBuilder/ReportBuilderGrid';
import ColumnConfigPanel from './reportBuilder/ColumnConfigPanel';
import CalculatedColumnDialog from './reportBuilder/CalculatedColumnDialog';
import ConditionalFormatDialog from './reportBuilder/ConditionalFormatDialog';
import {
  getCustomReports,
  createCustomReport,
  updateCustomReport,
  runCustomReportAdhoc,
  exportCustomReportAdhoc,
} from '../api';

// Column definitions by data source (same as in customReports backend)
const COLUMN_DEFS = {
  time_entries: [
    { key: 'employee_name', label: 'Employee' },
    { key: 'employee_email', label: 'Email' },
    { key: 'entry_date', label: 'Date' },
    { key: 'start_time', label: 'Start Time' },
    { key: 'end_time', label: 'End Time' },
    { key: 'break_minutes', label: 'Break (min)' },
    { key: 'hours_worked', label: 'Hours' },
    { key: 'hourly_rate', label: 'Rate' },
    { key: 'total_pay', label: 'Total Pay' },
    { key: 'company_name', label: 'Company' },
    { key: 'affiliate_name', label: 'Affiliate' },
    { key: 'department_name', label: 'Department' },
    { key: 'is_paid', label: 'Paid Status' },
    { key: 'paid_amount', label: 'Paid Amount' },
    { key: 'approval_status', label: 'Approval Status' },
    { key: 'notes', label: 'Notes' },
  ],
  expenses: [
    { key: 'employee_name', label: 'Employee' },
    { key: 'title', label: 'Title' },
    { key: 'expense_type', label: 'Type' },
    { key: 'status', label: 'Status' },
    { key: 'total_amount', label: 'Amount' },
    { key: 'start_date', label: 'Start Date' },
    { key: 'end_date', label: 'End Date' },
    { key: 'is_paid', label: 'Paid' },
    { key: 'notes', label: 'Notes' },
  ],
  invoices: [
    { key: 'invoice_number', label: 'Invoice #' },
    { key: 'company_name', label: 'Company' },
    { key: 'invoice_date', label: 'Date' },
    { key: 'due_date', label: 'Due Date' },
    { key: 'subtotal', label: 'Subtotal' },
    { key: 'tax_amount', label: 'Tax' },
    { key: 'total_amount', label: 'Total' },
    { key: 'status', label: 'Status' },
    { key: 'notes', label: 'Notes' },
  ],
};

// Default columns by data source
const DEFAULT_COLUMNS = {
  time_entries: ['employee_name', 'entry_date', 'hours_worked', 'total_pay', 'company_name'],
  expenses: ['employee_name', 'title', 'total_amount', 'status'],
  invoices: ['invoice_number', 'company_name', 'total_amount', 'status'],
};

export default function ReportBuilder() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const gridRef = useRef(null);

  // Report list
  const [reports, setReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState('');

  // Current report config
  const [reportName, setReportName] = useState('New Report');
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dataSource, setDataSource] = useState('time_entries');
  const [selectedColumns, setSelectedColumns] = useState(DEFAULT_COLUMNS.time_entries);
  const [columnOrder, setColumnOrder] = useState(DEFAULT_COLUMNS.time_entries);
  const columnOrderRef = useRef(DEFAULT_COLUMNS.time_entries); // tracks drag-reorder without re-render
  const [columnSettings, setColumnSettings] = useState({});
  const [calculatedColumns, setCalculatedColumns] = useState({});
  const [conditionalFormatting, setConditionalFormatting] = useState([]);
  const [groupConfig, setGroupConfig] = useState({ groupBy: null, showSubtotals: true, subtotalColumns: [] });
  const [showTotalsRow, setShowTotalsRow] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // UI state
  const [rowData, setRowData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(!isMobile);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [calcDialogOpen, setCalcDialogOpen] = useState(false);
  const [calcEditTarget, setCalcEditTarget] = useState(null);
  const [condDialogOpen, setCondDialogOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load reports
  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      const res = await getCustomReports();
      setReports(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load saved reports');
    }
  };

  // Select a report to edit
  const handleSelectReport = useCallback((id) => {
    setSelectedReportId(id);
    if (!id) {
      // New report
      setReportName('New Report');
      setDataSource('time_entries');
      setSelectedColumns(DEFAULT_COLUMNS.time_entries);
      setColumnOrder(DEFAULT_COLUMNS.time_entries);
      columnOrderRef.current = DEFAULT_COLUMNS.time_entries;
      setColumnSettings({});
      setCalculatedColumns({});
      setConditionalFormatting([]);
      setGroupConfig({ groupBy: null, showSubtotals: true, subtotalColumns: [] });
      setShowTotalsRow(false);
      setRowData(null);
      setHasChanges(false);
      return;
    }

    const report = reports.find(r => r.id === parseInt(id));
    if (!report) return;

    const config = report.config || {};
    setReportName(report.name);
    setDataSource(report.data_source || 'time_entries');
    setSelectedColumns(config.columns || DEFAULT_COLUMNS[report.data_source] || []);
    const loadedOrder = config.columnOrder || config.columns || [];
    setColumnOrder(loadedOrder);
    columnOrderRef.current = loadedOrder;
    setColumnSettings(config.columnSettings || {});
    setCalculatedColumns(config.calculatedColumns || {});
    setConditionalFormatting(config.conditionalFormatting || []);
    setGroupConfig(config.groupConfig || { groupBy: null, showSubtotals: true, subtotalColumns: [] });
    setShowTotalsRow(config.showTotalsRow || false);
    setRowData(null);
    setHasChanges(false);
  }, [reports]);

  const handleDataSourceChange = (ds) => {
    setDataSource(ds);
    setSelectedColumns(DEFAULT_COLUMNS[ds] || []);
    setColumnOrder(DEFAULT_COLUMNS[ds] || []);
    columnOrderRef.current = DEFAULT_COLUMNS[ds] || [];
    setColumnSettings({});
    setCalculatedColumns({});
    setGroupConfig({ groupBy: null, showSubtotals: true, subtotalColumns: [] });
    setRowData(null);
    setHasChanges(true);
  };

  const handleToggleColumn = (colKey) => {
    setSelectedColumns(prev => {
      const next = prev.includes(colKey) ? prev.filter(c => c !== colKey) : [...prev, colKey];
      setColumnOrder(order => {
        const newOrder = !prev.includes(colKey) ? [...order, colKey] : order.filter(c => c !== colKey);
        columnOrderRef.current = newOrder;
        return newOrder;
      });
      return next;
    });
    setHasChanges(true);
  };

  const handleColumnMoved = (newOrder) => {
    columnOrderRef.current = newOrder; // ref only — no re-render so grid keeps its position
    setHasChanges(true);
  };

  const handleColumnResized = (colId, width) => {
    setColumnSettings(prev => ({
      ...prev,
      [colId]: { ...prev[colId], width },
    }));
    setHasChanges(true);
  };

  const handleUpdateColumnSetting = (colKey, settings) => {
    setColumnSettings(prev => ({ ...prev, [colKey]: settings }));
    setHasChanges(true);
  };

  const handleSaveCalcColumn = (calc) => {
    setCalculatedColumns(prev => ({ ...prev, [calc.key]: calc }));
    // Add to column order if not present
    if (!columnOrderRef.current.includes(calc.key)) {
      setColumnOrder(prev => {
        const newOrder = [...prev, calc.key];
        columnOrderRef.current = newOrder;
        return newOrder;
      });
      setSelectedColumns(prev => [...prev, calc.key]);
    }
    setHasChanges(true);
  };

  const handleDeleteCalcColumn = (key) => {
    setCalculatedColumns(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setColumnOrder(prev => {
      const newOrder = prev.filter(c => c !== key);
      columnOrderRef.current = newOrder;
      return newOrder;
    });
    setSelectedColumns(prev => prev.filter(c => c !== key));
    setHasChanges(true);
  };

  const handleSaveCondRules = (rules) => {
    setConditionalFormatting(rules);
    setHasChanges(true);
  };

  // Build config object
  const buildConfig = () => ({
    version: 2,
    columns: selectedColumns,
    columnOrder: columnOrderRef.current,
    columnSettings,
    calculatedColumns,
    conditionalFormatting,
    groupConfig: groupConfig?.groupBy ? groupConfig : undefined,
    showTotalsRow,
    filters: ['dateRange', 'employee'],
    sort_column: 'entry_date',
    sort_order: 'DESC',
  });

  // Run preview — uses adhoc endpoint, never saves to database
  const handleRunPreview = async () => {
    try {
      setIsLoading(true);
      setError('');

      const config = buildConfig();
      const res = await runCustomReportAdhoc({
        data_source: dataSource,
        config,
        filters: {},
      });

      setHiddenColumns(res.data?.metadata?.hiddenColumns || []);
      if (res.data?.rows) {
        setRowData(res.data.rows.map(row => {
          const rawRow = {};
          (res.data.columns || []).forEach(colLabel => {
            const colDef = COLUMN_DEFS[dataSource]?.find(c => c.label === colLabel);
            if (colDef) {
              rawRow[colDef.key] = row[colLabel];
            } else {
              rawRow[colLabel] = row[colLabel];
            }
          });
          return rawRow;
        }));
      }
    } catch (err) {
      console.error('Preview error:', err);
      setError(err.response?.data?.error || 'Failed to run preview');
    } finally {
      setIsLoading(false);
    }
  };

  // Save report
  const handleSave = async () => {
    if (!reportName.trim()) {
      setError('Report name is required');
      return;
    }
    if (selectedColumns.length === 0) {
      setError('Select at least one column');
      return;
    }

    try {
      setIsSaving(true);
      setError('');

      const config = buildConfig();
      const payload = {
        name: reportName,
        description: '',
        data_source: dataSource,
        config,
      };

      if (selectedReportId) {
        await updateCustomReport(selectedReportId, payload);
        setSuccess('Report saved');
      } else {
        const res = await createCustomReport(payload);
        setSelectedReportId(String(res.data.id));
        setSuccess('Report created');
      }

      await loadReports();
      setHasChanges(false);
    } catch (err) {
      console.error('Save error:', err);
      setError(err.response?.data?.error || 'Failed to save report');
    } finally {
      setIsSaving(false);
    }
  };

  // Export — uses adhoc endpoint with current UI config
  const handleExport = async () => {
    try {
      setError('');
      const config = buildConfig();
      await exportCustomReportAdhoc({
        data_source: dataSource,
        config,
        name: reportName || 'Report',
        filters: {},
      });
    } catch (err) {
      console.error('Export error:', err);
      setError(err.message || 'Failed to export report');
    }
  };

  // Build the columns list including calculated columns
  const allColumns = [
    ...(COLUMN_DEFS[dataSource] || []).filter(c => selectedColumns.includes(c.key)),
    ...Object.entries(calculatedColumns).map(([key, calc]) => ({
      key,
      label: calc.headerName,
    })),
  ];

  // Available columns for the config panel
  const availableColumnsForPanel = COLUMN_DEFS[dataSource] || [];

  const configPanel = (
    <ColumnConfigPanel
      selectedColumn={selectedColumn}
      columnSettings={columnSettings}
      onUpdateColumnSetting={handleUpdateColumnSetting}
      availableColumns={availableColumnsForPanel}
      selectedColumns={selectedColumns}
      onToggleColumn={handleToggleColumn}
      onClose={() => setIsPanelOpen(false)}
      onOpenCalculatedDialog={() => { setCalcEditTarget(null); setCalcDialogOpen(true); }}
      onOpenConditionalDialog={() => setCondDialogOpen(true)}
      calculatedColumns={calculatedColumns}
      onDeleteCalculatedColumn={handleDeleteCalcColumn}
      groupConfig={groupConfig}
      onUpdateGroupConfig={(cfg) => { setGroupConfig(cfg); setHasChanges(true); }}
      showTotalsRow={showTotalsRow}
      onToggleTotalsRow={(val) => { setShowTotalsRow(val); setHasChanges(true); }}
    />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 220px)', minHeight: 400 }}>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 1 }}>{error}</Alert>}

      {hiddenColumns.length > 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {`Hidden: ${hiddenColumns.join(', ')} — needs the View pay rates permission`}
        </Alert>
      )}

      <ReportBuilderToolbar
        reports={reports}
        selectedReportId={selectedReportId}
        onSelectReport={handleSelectReport}
        reportName={reportName}
        onReportNameChange={(name) => { setReportName(name); setHasChanges(true); }}
        dataSource={dataSource}
        onDataSourceChange={handleDataSourceChange}
        onSave={handleSave}
        onRunPreview={handleRunPreview}
        onExport={handleExport}
        onNewReport={() => handleSelectReport('')}
        onTogglePanel={() => setIsPanelOpen(prev => !prev)}
        isPanelOpen={isPanelOpen}
        isLoading={isLoading}
        isSaving={isSaving}
        hasChanges={hasChanges}
      />

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Grid area */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {rowData === null ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', bgcolor: '#fafafa' }}>
              <Typography color="text.secondary">
                {isLoading ? 'Loading...' : 'Click "Preview" to load data'}
              </Typography>
            </Box>
          ) : (
            <ReportBuilderGrid
              rowData={rowData}
              columns={allColumns}
              columnSettings={columnSettings}
              columnOrder={columnOrder.filter(c => selectedColumns.includes(c) || calculatedColumns[c])}
              calculatedColumns={calculatedColumns}
              conditionalFormatting={conditionalFormatting}
              groupConfig={groupConfig?.groupBy ? groupConfig : undefined}
              showTotalsRow={showTotalsRow}
              onColumnMoved={handleColumnMoved}
              onColumnResized={handleColumnResized}
              onColumnSelected={setSelectedColumn}
              gridRef={gridRef}
            />
          )}
        </Box>

        {/* Config panel - desktop side panel */}
        {!isMobile && isPanelOpen && (
          <Box sx={{ width: 300, minWidth: 280, borderLeft: '1px solid #e0e0e0', overflow: 'hidden' }}>
            {configPanel}
          </Box>
        )}
      </Box>

      {/* Mobile - drawer for config panel */}
      {isMobile && (
        <>
          <Drawer
            anchor="bottom"
            open={isPanelOpen}
            onClose={() => setIsPanelOpen(false)}
            PaperProps={{ sx: { maxHeight: '60vh', borderTopLeftRadius: 12, borderTopRightRadius: 12 } }}
          >
            {configPanel}
          </Drawer>
          {!isPanelOpen && (
            <Fab
              size="small"
              color="primary"
              onClick={() => setIsPanelOpen(true)}
              sx={{ position: 'fixed', bottom: 16, right: 16 }}
            >
              <SettingsIcon />
            </Fab>
          )}
        </>
      )}

      {/* Calculated Column Dialog */}
      <CalculatedColumnDialog
        open={calcDialogOpen}
        onClose={() => setCalcDialogOpen(false)}
        availableColumns={COLUMN_DEFS[dataSource] || []}
        existingCalc={calcEditTarget}
        onSave={handleSaveCalcColumn}
      />

      {/* Conditional Formatting Dialog */}
      <ConditionalFormatDialog
        open={condDialogOpen}
        onClose={() => setCondDialogOpen(false)}
        columns={allColumns}
        rules={conditionalFormatting}
        onSave={handleSaveCondRules}
      />

      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} message={success} />
    </Box>
  );
}
